export type Bilingual = {
  en: string
  fr: string
}

export const identityHero = {
  eyebrow: {
    en: 'Deploy once. Compose forever.',
    fr: 'Déployez une fois. Ajoutez des capacités à volonté.',
  },
  headline: [
    { en: 'Identity isn’t a feature.', fr: 'L’identité n’est pas une fonctionnalité.' },
    { en: 'It’s the architecture.', fr: 'C’est l’architecture.' },
  ],
  body: [
    {
      en: 'A trusted identity connects people, relationships, rights and consents to services, transactions and intelligence.',
      fr: 'Une identité de confiance relie les personnes, leurs relations, leurs droits et leurs consentements aux services, aux transactions et à l’intelligence.',
    },
    {
      en: 'Deploy it once. It becomes a reusable foundation for every new capability.',
      fr: 'Déployée une fois, elle devient une fondation réutilisable pour chaque nouvelle capacité.',
    },
  ],
  primaryCta: { en: 'Request a demo', fr: 'Demander une démo' },
  secondaryCta: {
    en: 'See identity in action',
    fr: 'Voir l’identité en action',
  },
}

export const identityFoundation = {
  label: { en: 'Open identity', fr: 'Identité ouverte' },
  title: { en: 'One trusted identity', fr: 'Une identité de confiance' },
  items: {
    en: 'Profile · Relationships · Rights · Consents · Credentials',
    fr: 'Profil · Relations · Droits · Consentements · Attestations',
  },
}

export type CapabilityIcon = 'message' | 'services' | 'card' | 'chart'

export const identityCapabilities: {
  id: string
  icon: CapabilityIcon
  title: Bilingual
  items: Bilingual
}[] = [
  {
    id: 'communications',
    icon: 'message',
    title: { en: 'Communications', fr: 'Communications' },
    items: {
      en: 'Notices · Emergency · Notifications',
      fr: 'Avis · Urgence · Notifications',
    },
  },
  {
    id: 'services',
    icon: 'services',
    title: { en: 'Services', fr: 'Services' },
    items: {
      en: 'Recreation · Library · Requests · Permits',
      fr: 'Loisirs · Bibliothèque · Requêtes · Permis',
    },
  },
  {
    id: 'transactions',
    icon: 'card',
    title: { en: 'Transactions', fr: 'Transactions' },
    items: {
      en: 'Payments · Registrations · Fees',
      fr: 'Paiements · Inscriptions · Redevances',
    },
  },
  {
    id: 'atna',
    icon: 'chart',
    title: { en: 'ATNA Intelligence', fr: 'Intelligence ATNA' },
    items: {
      en: 'Analytics · Recommendations · Automation',
      fr: 'Analyses · Recommandations · Automatisation',
    },
  },
]

export const identityPlatform = {
  label: { en: 'API & orchestration', fr: 'API et orchestration' },
  title: {
    en: 'One open, integrated platform',
    fr: 'Une plateforme ouverte et intégrée',
  },
  items: {
    en: 'Microservices · APIs · Events · Connectors · Security',
    fr: 'Microservices · API · Événements · Connecteurs · Sécurité',
  },
}

export const identityAtna = {
  label: {
    en: 'ATNA — trusted intelligence',
    fr: 'ATNA — intelligence de confiance',
  },
  title: {
    en: 'Understands · Verifies · Orchestrates · Acts',
    fr: 'Comprend · Vérifie · Orchestre · Agit',
  },
  items: {
    en: 'Verified identity · Contextual data · Full traceability',
    fr: 'Identité vérifiée · Données contextuelles · Traçabilité complète',
  },
}

export type SystemIcon =
  | 'book'
  | 'run'
  | 'file'
  | 'permit'
  | 'parking'
  | 'water'
  | 'alert'

export const identitySystems: {
  id: string
  icon: SystemIcon
  label: Bilingual
}[] = [
  { id: 'libraries', icon: 'book', label: { en: 'Libraries', fr: 'Bibliothèques' } },
  { id: 'recreation', icon: 'run', label: { en: 'Recreation', fr: 'Loisirs' } },
  { id: 'requests', icon: 'file', label: { en: '311 / Requests', fr: '311 / Requêtes' } },
  { id: 'permits', icon: 'permit', label: { en: 'Permits', fr: 'Permis' } },
  { id: 'parking', icon: 'parking', label: { en: 'Parking', fr: 'Stationnement' } },
  { id: 'water', icon: 'water', label: { en: 'Water', fr: 'Eau' } },
  { id: 'emergency', icon: 'alert', label: { en: 'Emergency', fr: 'Urgence' } },
]

export const identitySystemsLabel = {
  en: 'External systems',
  fr: 'Systèmes externes',
}

export const identityFooter = {
  counts: {
    en: '21 connectors · 7 domains · 6 protocols',
    fr: '21 connecteurs · 7 domaines · 6 protocoles',
  },
  protocols: 'REST/HTTP · SIP2 · SOAP · XML · OData · FTP/SFTP',
}
